// Client-side truncation for track() (spec §5.1): name ≤ 128 UTF-16 chars
// (after NFC), ≤ 100 keys, ≤ 8 KB serialized in UTF-8 BYTES. Mirrors the
// server's validate-track limits exactly — the server checks
// Buffer.byteLength(JSON.stringify(...), 'utf8') and
// name.trim().normalize('NFC').length — so a sanitized event is never
// rejected at ingest. Pure — unit-tested mock-free.
export const TRACK_NAME_MAX_CHARS = 128;
export const TRACK_MAX_KEYS = 100;
export const TRACK_MAX_BYTES = 8192;

export interface SanitizedTrack {
  name: string;
  properties: Record<string, unknown>;
  droppedKeys: string[];
  nameTruncated: boolean;
}

const utf8 = new TextEncoder();

// UTF-8 byte size of the JSON serialization — NOT .length, which counts
// UTF-16 code units and undercounts non-ASCII (Cyrillic/CJK 2-3x, emoji 2x):
// a payload that passes a .length check can still blow the server's byte
// budget and get silently rejected.
function serializedByteSize(value: unknown): number {
  try {
    const s = JSON.stringify(value);
    // JSON.stringify(undefined) returns undefined — treat as undroppable-max
    // so unserializable values get dropped first.
    return s === undefined ? Number.MAX_SAFE_INTEGER : utf8.encode(s).length;
  } catch {
    return Number.MAX_SAFE_INTEGER; // circular / BigInt
  }
}

// Truncate to at most TRACK_NAME_MAX_CHARS UTF-16 units without ever
// splitting a surrogate pair: walk code points, stop before the one that
// would cross the limit. An emoji at the boundary is dropped whole instead
// of leaving a lone high surrogate (which is invalid UTF-8 on the wire).
function truncateName(normalized: string): string {
  if (normalized.length <= TRACK_NAME_MAX_CHARS) return normalized;
  let out = '';
  for (const codePoint of normalized) {
    if (out.length + codePoint.length > TRACK_NAME_MAX_CHARS) break;
    out += codePoint;
  }
  return out;
}

interface SizedEntry {
  key: string;
  value: unknown;
  /** UTF-8 bytes of JSON.stringify(value); MAX_SAFE_INTEGER if unserializable. */
  valueBytes: number;
  /** Full cost of this entry inside the object: "key": + value. */
  entryBytes: number;
}

export function sanitizeTrackInput(
  name: string,
  properties?: Record<string, unknown>,
): SanitizedTrack {
  // Match the server's order: trim → NFC → length check. Normalizing FIRST
  // means a decomposed name (e + combining accent) that composes under 128
  // chars is NOT truncated — same as the server.
  const normalized = name.trim().normalize('NFC');
  const finalName = truncateName(normalized);
  const droppedKeys: string[] = [];
  const kept: SizedEntry[] = [];

  if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
    const entries = Object.entries(properties);
    for (const [key, value] of entries.slice(0, TRACK_MAX_KEYS)) {
      kept.push({
        key,
        value,
        valueBytes: serializedByteSize(value),
        entryBytes: serializedByteSize(key) + 1 + serializedByteSize(value),
      });
    }
    for (const [key] of entries.slice(TRACK_MAX_KEYS)) droppedKeys.push(key);

    // Serialized budget: drop the LARGEST value until it fits — keeps the
    // most keys while shedding the payload offenders deterministically.
    // Sizes are computed once per entry and the total maintained
    // incrementally (O(n) per evict instead of re-stringifying the object):
    // total = {braces} + Σ entryBytes + (n-1) commas — exact for the flat
    // object JSON.stringify produces.
    let totalBytes = 2;
    for (const entry of kept) totalBytes += entry.entryBytes;
    if (kept.length > 1) totalBytes += kept.length - 1;

    while (totalBytes > TRACK_MAX_BYTES && kept.length > 0) {
      let largestIdx = 0;
      for (let i = 1; i < kept.length; i++) {
        if (kept[i].valueBytes > kept[largestIdx].valueBytes) largestIdx = i;
      }
      const [evicted] = kept.splice(largestIdx, 1);
      // Removing an entry also removes one separating comma when others remain.
      totalBytes -= evicted.entryBytes + (kept.length > 0 ? 1 : 0);
      droppedKeys.push(evicted.key);
    }
  }

  const out: Record<string, unknown> = {};
  for (const entry of kept) out[entry.key] = entry.value;

  return {
    name: finalName,
    properties: out,
    droppedKeys,
    nameTruncated: finalName.length !== normalized.length,
  };
}
